/**
 * WebSocket Context Provider
 *
 * Centralizes WebSocket management to ensure a single connection per session.
 * All components access WebSocket via useWebSocketContext() hook.
 */

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { logger } from "../lib/logger";
import { useChatHandler } from "../lib/useChatHandler";
import {
  useWebSocket,
  ConnectionStatus,
  type ConnectionStatusType,
} from "../lib/useWebSocket";
import { useGameStore } from "../store/gameStore";
import type { ClientActionEnum, LobbyState, GameState } from "../types/game";

// Type for player objects in state
type PlayerWithId = { player_id: string };
type PlayerWithProfile = PlayerWithId & {
  profile_emoji?: string | null;
  theme_primary_color?: string | null;
};

// Type for game state with player arrays
type StateWithPlayers = {
  current_players?: PlayerWithId[];
  all_players?: PlayerWithId[];
  room_status?: string;
  phase?: string; // Backend may send phase instead of room_status
};

interface WebSocketContextValue {
  sendMessage: (
    action: ClientActionEnum | string,
    payload?: Record<string, unknown>,
  ) => void;
  status: ConnectionStatusType;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sessionId,
    playerId,
    playerToken,
    updateGameState,
    setSessionError,
    clearSession,
    addSpectator,
    removeSpectator,
    updateLobbySettings,
    setAllowSpectators,
    addPlayer,
    removePlayer,
    updatePlayerProfile,
    reorderPlayersForHost,
  } = useGameStore();
  const { handleChatMessage, isChatAction } = useChatHandler();

  // Centralized WebSocket message handler
  // This persists across Lobby → Game transitions
  const handleCentralMessage = useCallback(
    (message: { action: string; payload: Record<string, unknown> }) => {
      // Handle chat messages first
      if (isChatAction(message.action)) {
        handleChatMessage(message);
        return;
      }

      // Most messages just update the game state
      if (message.action === "GAME_STATE_UPDATE") {
        // Backend sends { state: {...}, type: 'full' } - extract actual state
        const payload = message.payload as { state?: unknown };
        const actualState = (payload.state || payload) as StateWithPlayers;

        // Log raw message and state for navigation debugging
        logger.debug("ws_game_state_update_received", {
          hasState: "state" in (message.payload as Record<string, unknown>),
          room_status: actualState.room_status,
          phase: actualState.phase,
        });

        updateGameState(actualState as GameState | LobbyState);

        // Check if game has started (transitioned out of lobby)
        // Navigate if: game started AND not already on game page
        // Backend may send room_status OR phase depending on the message source
        const status = actualState.room_status || actualState.phase;
        const isGameStarted =
          status && status !== "lobby" && status !== "LOBBY";
        const isOnGamePage = location.pathname.startsWith("/game/");

        // Log navigation decision for debugging
        logger.debug("ws_navigation_check", {
          status,
          isGameStarted,
          isOnGamePage,
          locationPathname: location.pathname,
          sessionId,
          willNavigate: isGameStarted && !isOnGamePage && sessionId,
        });

        if (isGameStarted && !isOnGamePage && sessionId) {
          // Game started - navigate to game view from anywhere
          logger.info("navigating_to_game", {
            from: location.pathname,
            sessionId,
            roomStatus: status,
          });
          navigate(`/game/${sessionId}`);
        }
      }
      // Player joined/left - use atomic store operations to avoid race conditions
      else if (message.action === "PLAYER_JOINED_WS") {
        const newPlayer = message.payload as PlayerWithId & {
          display_name?: string;
          is_npc?: boolean;
        };
        addPlayer(newPlayer as Parameters<typeof addPlayer>[0]);
      } else if (message.action === "PLAYER_LEFT_WS") {
        const leftPlayerId = (message.payload as { player_id: string })
          .player_id;
        removePlayer(leftPlayerId);
      }
      // Handle delta sync (for now just log - proper delta handling is complex)
      else if (message.action === "GAME_STATE_DELTA") {
        logger.debug("websocket_delta_not_supported");
      }
      // Other common messages
      else if (message.action === "ERROR_MESSAGE") {
        const errorDetail =
          (message.payload as { detail?: string }).detail ||
          "An error occurred";
        if (
          errorDetail.includes("terminated by administrator") ||
          errorDetail.includes("cleared by administrator") ||
          errorDetail.includes("Session not found")
        ) {
          setSessionError(errorDetail);
        }
      } else if (message.action === "SPECTATOR_JOINED") {
        const spectator = message.payload as unknown as {
          player_id: string;
          display_name: string;
        };
        addSpectator(spectator as Parameters<typeof addSpectator>[0]);
      } else if (message.action === "SPECTATOR_LEFT") {
        const spectatorId = (message.payload as { player_id: string })
          .player_id;
        removeSpectator(spectatorId);
      } else if (message.action === "LOBBY_SETTINGS_UPDATED") {
        updateLobbySettings(
          message.payload as Parameters<typeof updateLobbySettings>[0],
        );
      } else if (message.action === "PLAYER_PROFILE_UPDATED") {
        // Update player profile (emoji/color) using atomic store operation
        const profileUpdate = message.payload as PlayerWithProfile;
        updatePlayerProfile(profileUpdate.player_id, {
          profile_emoji: profileUpdate.profile_emoji,
          theme_primary_color: profileUpdate.theme_primary_color,
        });
      } else if (message.action === "SPECTATOR_MODE_CHANGED") {
        setAllowSpectators(
          (message.payload as { allow_spectators: boolean }).allow_spectators,
        );
      } else if (message.action === "HOST_REASSIGNED") {
        const hostPayload = message.payload as {
          new_host_name: string;
          new_host_id: string;
        };
        const newHostName = hostPayload.new_host_name;
        const newHostId = hostPayload.new_host_id;

        toast.info(`Host changed to ${newHostName}`, {
          description: "The previous host disconnected",
        });

        // Update local state to reflect new host using atomic store operation
        reorderPlayersForHost(newHostId, newHostId === playerId);
      } else if (message.action === "ROOM_CLOSING") {
        const reason =
          (message.payload as { reason?: string }).reason || "Room is closing";
        toast.warning("Room is closing", { description: reason });
        setSessionError(`Room closing: ${reason}`);
      }
      // For all other messages, child components will handle via their own logic
      // They can subscribe to store changes or handle via custom hooks
    },
    [
      updateGameState,
      setSessionError,
      addSpectator,
      removeSpectator,
      updateLobbySettings,
      setAllowSpectators,
      addPlayer,
      removePlayer,
      updatePlayerProfile,
      reorderPlayersForHost,
      isChatAction,
      handleChatMessage,
      location.pathname,
      navigate,
      sessionId,
      playerId,
    ],
  );

  // Handle fatal WebSocket errors (session not found, player not found)
  const handleFatalError = useCallback(
    (code: number, reason: string) => {
      logger.error("websocket_fatal_error", { code, reason });
      // Show error modal - it has its own "Return to Home" button
      // Don't auto-navigate so users can see what went wrong
      setSessionError(`Connection failed: ${reason || "Session not found"}`);
      // Clear session data to prevent further WebSocket reconnection attempts
      clearSession();
      // Note: We don't navigate('/') here - the ErrorModal handles navigation
      // when the user clicks "Return to Home". This ensures users see the error.
    },
    [clearSession, setSessionError],
  );

  // WebSocket connection - managed at context level to persist across route changes
  const { sendMessage, status } = useWebSocket({
    sessionId: sessionId || "",
    playerId: playerId || "",
    playerToken: playerToken || "",
    onMessage: handleCentralMessage,
    onFatalError: handleFatalError,
  });

  // Listen for profile updates from Settings page to broadcast to current session
  useEffect(() => {
    const handleProfileUpdate = (
      event: CustomEvent<{
        profile_emoji?: string;
        theme_primary_color?: string;
      }>,
    ) => {
      if (status === ConnectionStatus.CONNECTED && sessionId) {
        const { profile_emoji, theme_primary_color } = event.detail;
        sendMessage("UPDATE_PLAYER_PROFILE", {
          profile_emoji,
          theme_primary_color,
        });
      }
    };

    window.addEventListener(
      "profile-updated",
      handleProfileUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        "profile-updated",
        handleProfileUpdate as EventListener,
      );
    };
  }, [status, sessionId, sendMessage]);

  const value = useMemo<WebSocketContextValue>(
    () => ({
      sendMessage,
      status,
      isConnected: status === ConnectionStatus.CONNECTED,
    }),
    [sendMessage, status],
  );

  // Expose sendMessage on window for E2E testing
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as unknown as Record<string, unknown>).__WS_SEND_MESSAGE__ =
        sendMessage;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete (window as unknown as Record<string, unknown>)
          .__WS_SEND_MESSAGE__;
      }
    };
  }, [sendMessage]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      "useWebSocketContext must be used within WebSocketProvider",
    );
  }
  return context;
}

// Re-export ConnectionStatus for convenience
export { ConnectionStatus };
